<template>
  <div class="app-shell h-full flex overflow-hidden">
    <!-- ===== SIDEBAR (nav rail) ===== -->
    <aside class="app-rail flex-shrink-0 h-full flex flex-col overflow-hidden">
      <!-- Brand → goes to all files -->
      <router-link
        :to="{ name: 'files-list', params: { state: 'all' } }"
        class="app-rail__brand"
      >
        <span class="app-logo">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 2.5 20.5 7v10L12 21.5 3.5 17V7L12 2.5Z"
              fill="#111111"
            />
            <path d="M12 2.5 20.5 7 12 11.5 3.5 7 12 2.5Z" fill="#2b2b2b" />
            <path d="M12 11.5 20.5 7v10L12 21.5v-10Z" fill="#000000" />
          </svg>
        </span>
        <span class="app-rail__brand-name">Transcriber</span>
      </router-link>

      <!-- Nav — three expandable top-level sections -->
      <nav class="app-rail__nav flex-1 overflow-y-auto min-h-0">
        <template v-for="s in sections" :key="s.name">
          <!-- Section header — router-link for routed sections… -->
          <router-link
            v-if="s.to"
            :to="s.to"
            class="app-nav-item app-nav-item--section"
            :class="{ 'app-nav-item--active': isSectionActive(s.name) }"
          >
            <component
              :is="navIcon(s.icon)"
              class="app-nav-item__icon shrink-0"
            />
            <span class="flex-1 truncate">{{ s.title }}</span>
            <chevron-down
              v-if="s.expandable"
              class="app-nav-item__chevron shrink-0"
              :class="{ 'app-nav-item__chevron--open': isSectionExpanded(s) }"
            />
          </router-link>
          <!-- …plain button for the routeless "Материалы" section -->
          <button
            v-else
            type="button"
            class="app-nav-item app-nav-item--section"
            @click="toggleMaterials()"
          >
            <component
              :is="navIcon(s.icon)"
              class="app-nav-item__icon shrink-0"
            />
            <span class="flex-1 truncate">{{ s.title }}</span>
            <chevron-down
              v-if="s.expandable"
              class="app-nav-item__chevron shrink-0"
              :class="{ 'app-nav-item__chevron--open': isSectionExpanded(s) }"
            />
          </button>

          <!-- Expanding sub-items -->
          <transition name="rail-expand">
            <div v-if="isSectionExpanded(s)" class="app-rail__sub">
              <template v-for="(item, i) in s.children" :key="'i' + i">
                <!-- Internal route child -->
                <router-link
                  v-if="!item.external"
                  :to="item.to"
                  class="app-nav-item app-nav-item--sub"
                  :class="{ 'app-nav-item--active': isChildActive(item.to) }"
                >
                  <component
                    v-if="item.iconName"
                    :is="childIcon(item.iconName)"
                    class="app-nav-item__icon shrink-0"
                  />
                  <span class="flex-1 truncate">{{ item.title }}</span>
                </router-link>

                <!-- External link child -->
                <a
                  v-else
                  :href="item.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="app-nav-item app-nav-item--sub"
                >
                  <span class="flex-1 truncate">{{ item.title }}</span>
                  <arrow-right-up class="app-nav-item__ext shrink-0" />
                </a>

                <!-- Third-level tabs under "Отчеты" -->
                <template
                  v-if="item.to && item.to.name === 'reports' && inReports"
                >
                  <router-link
                    v-for="(tab, t) in reportTabs"
                    :key="'rt' + t"
                    :to="tab.to"
                    class="app-nav-item app-nav-item--sub app-nav-item--tab"
                    :class="{ 'app-nav-item--active': isChildActive(tab.to) }"
                  >
                    <span class="flex-1 truncate">{{ tab.title }}</span>
                  </router-link>
                </template>
              </template>
            </div>
          </transition>
        </template>
      </nav>
    </aside>

    <!-- ===== MAIN (white island) ===== -->
    <main class="app-main flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
      <!-- Top bar: breadcrumbs (left) + user card (right) -->
      <div class="app-topbar flex-shrink-0">
        <div class="app-crumbs">
          <svg
            class="app-crumbs__doc"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
              stroke="currentColor"
              stroke-width="1.6"
            />
            <path d="M14 3v4h4" stroke="currentColor" stroke-width="1.6" />
          </svg>
          <template v-for="(c, i) in breadcrumbs" :key="i">
            <svg
              class="app-crumbs__sep"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m9 6 6 6-6 6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span
              class="app-crumbs__item"
              :class="{
                'app-crumbs__item--current': i === breadcrumbs.length - 1,
              }"
              >{{ c }}</span
            >
          </template>
        </div>

        <div class="app-topbar__right">
          <button
            class="theme-toggle"
            type="button"
            :title="dark ? 'Светлая тема' : 'Тёмная тема'"
            @click="onToggleTheme"
          >
            <svg
              v-if="dark"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="4.5" />
              <path
                d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
              />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
            </svg>
          </button>
          <SidebarUserCard />
        </div>
      </div>

      <div class="flex-1 min-h-0 overflow-hidden">
        <router-view class="h-full overflow-hidden" />
      </div>
    </main>
  </div>
</template>

<script>
import { markRaw, defineAsyncComponent } from "vue";
import ArrowRightUp from "@/icons/ArrowRightUp.vue";
import ChevronDown from "@/icons/ChevronDown.vue";
import FileIcon from "@/icons/FileIcon.vue";
import ManagementIcon from "@/icons/ManagementIcon.vue";
import BooksIcon from "@/icons/BooksIcon.vue";
import SidebarUserCard from "@/components/SidebarUserCard.vue";
import { getSections } from "@/views/files/shared/sections";
import { sidebarItems as manageItems } from "@/views/manage/shared/sidebar";
import { isDark, toggleTheme } from "@/services/theme";

export default {
  name: "default",
  components: {
    SidebarUserCard,
    ArrowRightUp,
    ChevronDown,
  },

  data() {
    return {
      dark: isDark(),
      // "Материалы" has no route of its own — it expands on click.
      materialsOpen: false,
    };
  },

  computed: {
    shortLink() {
      if (this.domain === "https://kt.tr.q19.kz") {
        return "https://u.to/B2ZYIg";
      }
      return "https://u.to/SGsFIQ";
    },
    wordPatterns() {
      return "https://u.to/UqViIg";
    },
    stationsList() {
      return "https://u.to/GVBpIg";
    },
    isKazTelecom() {
      return window.location.hostname === "kt.tr.q19.kz";
    },
    isKTZ() {
      return window.location.hostname === "ktz.tr.q19.kz";
    },
    domain() {
      return window.domain;
    },
    user() {
      return this.$store.state.user.user;
    },
    // Three expandable top-level sections: Файлы, Управление, Материалы.
    // Each carries its own `children`; the active (or opened) one expands
    // its sub-items indented beneath it — no sub-headings.
    sections() {
      if (!this.user) return [];
      const { scopes } = this.user;
      const isAdmin = scopes.includes("admin");
      const list = [];

      // Файлы — statuses as children (scope-filtered).
      list.push({
        name: "files",
        title: "Файлы",
        icon: "files",
        to: { name: "files" },
        expandable: true,
        children: getSections(scopes[0]),
      });

      // Управление — admin only.
      if (isAdmin) {
        list.push({
          name: "manage",
          title: "Управление",
          icon: "manage",
          to: { name: "manage" },
          expandable: true,
          children: manageItems,
        });
      }

      // Материалы — external resources, expands on click (no own route).
      const materials = this.materialsChildren;
      if (materials.length) {
        list.push({
          name: "materials",
          title: "Материалы",
          icon: "materials",
          expandable: true,
          children: materials,
        });
      }

      return list;
    },
    // External resource links grouped under "Материалы".
    materialsChildren() {
      if (!this.user) return [];
      const { scopes } = this.user;
      const items = [];
      if (scopes.includes("verifier") || scopes.includes("admin")) {
        items.push({
          title: "Поиск по словарю",
          external: true,
          href: this.$router.resolve({ name: "dictionary" }).href,
        });
      }
      items.push({
        title: "Правила разметки",
        external: true,
        href: this.shortLink,
      });
      if (this.isKazTelecom) {
        items.push({
          title: "Примеры слов",
          external: true,
          href: this.wordPatterns,
        });
      }
      if (this.isKTZ) {
        items.push({
          title: "Список станций",
          external: true,
          href: this.stationsList,
        });
      }
      return items;
    },
    // Whether we are currently inside the Reports section.
    inReports() {
      return this.$route.matched.some((r) => r.name === "reports");
    },
    // Third-level tabs shown under "Отчеты" when that section is active.
    reportTabs() {
      if (!this.inReports) return [];
      return [
        { title: "Сводный", to: { name: "summary_reports" } },
        { title: "Аннотаторы", to: { name: "annotator_reports" } },
        { title: "Размеченные файлы", to: { name: "marked_reports" } },
        { title: "Верификаторы", to: { name: "verifier_reports" } },
        { title: "Верифицированные файлы", to: { name: "verified_reports" } },
        { title: "Словарь", to: { name: "dictionary_reports" } },
      ];
    },
    // Breadcrumb labels for the current route (rendered inside the island).
    breadcrumbs() {
      const labels = {
        files: "Файлы",
        "files-list": "Список",
        "file-view": "Просмотр файла",
        manage: "Управление",
        "users-list": "Пользователи",
        tarif: "Тарифы",
        settings: "Настройки",
        reports: "Отчеты",
        dictionary: "Поиск по словарю",
        Home: "Главная",
      };
      const crumbs = this.$route.matched
        .map((r) => labels[r.name] || (r.name ? String(r.name) : ""))
        .filter(Boolean);
      // De-duplicate consecutive equal labels (parent redirect → child).
      return crumbs.filter((c, i) => c !== crumbs[i - 1]);
    },
  },
  methods: {
    onToggleTheme() {
      this.dark = toggleTheme() === "dark";
    },
    navIcon(name) {
      const map = {
        files: FileIcon,
        manage: ManagementIcon,
        materials: BooksIcon,
      };
      return markRaw(map[name] || FileIcon);
    },
    toggleMaterials() {
      this.materialsOpen = !this.materialsOpen;
    },
    // A section is expanded when its route group is active (Файлы / Управление)
    // or, for "Материалы", when it has been toggled open.
    isSectionExpanded(s) {
      if (s.name === "materials") return this.materialsOpen;
      return this.isSectionActive(s.name);
    },
    childIcon(name) {
      if (!name) return markRaw(FileIcon);
      return markRaw(defineAsyncComponent(() => import(`@/icons/${name}.vue`)));
    },
    // A section is active when the current route belongs to its route group.
    isSectionActive(name) {
      return this.$route.matched.some((r) => r.name === name);
    },
    // A child is active when its resolved path matches the current path.
    isChildActive(to) {
      const resolved = this.$router.resolve(to);
      return (
        this.$route.path === resolved.path ||
        this.$route.fullPath === resolved.href
      );
    },
  },
};
</script>

<style lang="scss" scoped>
/* ===== Taskk-style shell — sizes ~50% of the reference mockup ===== */
.app-shell {
  background: var(--canvas);
}

/* ===== Sidebar rail ===== */
.app-rail {
  width: 252px;
  padding: 12px 12px;

  &__brand {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 4px 6px;
    margin-bottom: 12px;
    text-decoration: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    &:hover {
      background: var(--accent);
    }
  }

  &__brand-name {
    font-size: 17px;
    font-weight: 700;
    letter-spacing: -0.4px;
    color: var(--foreground);
  }

  &__nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0;
  }

  /* Indented container for a section's sub-items */
  &__sub {
    display: flex;
    flex-direction: column;
    gap: 1px;
    // guide rail sits under the parent icon; children align to its right
    margin: 2px 0 4px 18px;
    padding-left: 10px;
    border-left: 1px solid var(--border);
    overflow: hidden;
  }
}

.app-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;

  svg {
    width: 30px;
    height: 30px;
    display: block;
  }
}

/* Nav item — flat row; active = clean white pill with soft shadow */
.app-nav-item {
  display: flex;
  align-items: center;
  gap: 9px;
  border-radius: 8px;
  white-space: nowrap;
  text-decoration: none;
  color: var(--muted-foreground);
  height: 33px;
  padding: 0 9px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;

  &__icon {
    width: 15px;
    height: 15px;
    color: var(--muted-foreground);
    stroke-width: 1.8;
  }
  &__ext {
    width: 12px;
    height: 12px;
    color: var(--muted-foreground);
  }
  &__chevron {
    width: 13px;
    height: 13px;
    color: var(--muted-foreground);
    transition: transform 0.2s ease;
    &--open {
      transform: rotate(180deg);
    }
  }

  &:hover:not(.app-nav-item--active) {
    background: var(--accent);
    color: var(--accent-foreground);
  }

  // top-level section header: full-width button, reset native styles
  &--section {
    width: 100%;
    background: transparent;
    border: 0;
    font-family: inherit;
  }

  // sub-items: lighter weight, indentation handled by .app-rail__sub
  &--sub {
    height: 31px;
    font-size: 12px;
  }

  // third-level tabs (e.g. report tabs) sit one level deeper
  &--tab {
    margin-left: 14px;
  }

  &--active {
    background: var(--card);
    color: var(--foreground);
    font-weight: 700;
    border: 1px solid var(--border);
    padding: 0 8px; // −1px each side to compensate for the border
    box-shadow: var(--shadow-pill);
    .app-nav-item__icon,
    .app-nav-item__chevron {
      color: var(--foreground);
    }
  }
}

/* Expand / collapse animation for a section's sub-items */
.rail-expand-enter-active,
.rail-expand-leave-active {
  transition: max-height 0.24s ease, opacity 0.2s ease, margin 0.24s ease;
}
.rail-expand-enter-from,
.rail-expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  margin-bottom: 0;
}
.rail-expand-enter-to,
.rail-expand-leave-from {
  max-height: 520px;
  opacity: 1;
}

/* Main content island — floats on the gray canvas with a gap all around */
.app-main {
  background: var(--card);
  margin: 12px 12px 12px 0;
  border-radius: 18px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

/* Top bar — breadcrumbs left, user card right */
.app-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 18px 6px 26px;

  &__right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
}

/* Theme toggle */
.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--muted-foreground);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    border-color 0.15s ease;

  svg {
    width: 17px;
    height: 17px;
  }

  &:hover {
    background: var(--accent);
    color: var(--foreground);
    border-color: var(--border-strong);
  }
}

/* Logo cube — invert in dark so it stays visible on the dark canvas */
:global(html.dark) .app-logo svg path {
  &[fill="#111111"],
  &[fill="#000000"] {
    fill: #fafafa;
  }
  &[fill="#2b2b2b"] {
    fill: #d4d4d8;
  }
}

/* Breadcrumbs — top of the island, above all content */
.app-crumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  &__doc {
    width: 16px;
    height: 16px;
    color: var(--muted-foreground);
    flex-shrink: 0;
  }
  &__sep {
    width: 13px;
    height: 13px;
    color: var(--border-strong);
    flex-shrink: 0;
  }
  &__item {
    font-size: 13px;
    font-weight: 500;
    color: var(--muted-foreground);
    white-space: nowrap;

    &--current {
      color: var(--foreground);
      font-weight: 600;
      background: var(--muted);
      padding: 4px 12px;
      border-radius: 9px;
    }
  }
}
</style>
